import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  Coins, LayoutDashboard, History, LogOut, ShieldCheck, 
  Menu, X, Sparkles, User, RefreshCw, Send, HelpCircle
} from 'lucide-react';
import { sellerAPI } from '../utils/api';

const SellerLayout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [seller, setSeller] = useState(null);
  const [balance, setBalance] = useState(0);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchSellerProfile();
  }, []);

  const fetchSellerProfile = async () => {
    setLoadingBalance(true);
    try {
      const res = await sellerAPI.getProfile();
      if (res.data?.success) {
        setSeller(res.data.data);
        setBalance(res.data.data.coins_balance || 0);
      }
    } catch (err) {
      console.error("Failed to load seller info:", err);
    } finally {
      setLoadingBalance(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('sellerToken');
    navigate('/seller/login');
  };

  const navigation = [
    { name: 'Dashboard & Add Wallet', href: '/seller/dashboard', icon: LayoutDashboard },
    { name: 'Direct Coin Transfer', href: '/seller/wallet', icon: Send },
    { name: 'Sales & Transactions', href: '/seller/history', icon: History },
  ];

  return (
    <div className="min-h-screen bg-[#070b19] text-slate-100 font-['Outfit'] flex flex-col md:flex-row overflow-hidden relative">
      {/* Dynamic Background Glows */}
      <div className="fixed top-[-10%] left-[-10%] w-[500px] h-[500px] bg-amber-500/10 blur-[130px] rounded-full pointer-events-none"></div>
      <div className="fixed bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-pink-600/10 blur-[130px] rounded-full pointer-events-none"></div>

      {/* Mobile Top Navbar */}
      <div className="md:hidden flex items-center justify-between p-4 bg-[#0d142b]/80 backdrop-blur-xl border-b border-white/10 z-40 sticky top-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-amber-500 to-yellow-300 p-[2px]">
            <div className="w-full h-full bg-[#0d142b] rounded-[14px] flex items-center justify-center">
              <Coins size={22} className="text-amber-400" />
            </div>
          </div>
          <div>
            <h1 className="font-black text-white text-base tracking-wide flex items-center gap-1">
              XOXO <span className="text-amber-400">SELLER</span>
            </h1>
            <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">Coin Merchant</p>
          </div>
        </div>

        <button 
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2.5 rounded-xl bg-white/5 border border-white/10 text-slate-300 hover:text-white"
        >
          {isSidebarOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Sidebar Navigation */}
      <div className={`
        fixed md:static inset-0 z-50 md:z-30 w-72 bg-[#0c1227]/95 md:bg-[#0c1227]/60 backdrop-blur-2xl 
        border-r border-white/10 flex flex-col justify-between p-6 transition-transform duration-300
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <div>
          {/* Brand Logo (Desktop) */}
          <div className="hidden md:flex items-center gap-3.5 mb-8 px-2">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-amber-500 via-yellow-400 to-amber-600 p-[2px] shadow-lg shadow-amber-500/20">
              <div className="w-full h-full bg-[#0a0f24] rounded-[14px] flex items-center justify-center">
                <Coins size={26} className="text-amber-400 animate-pulse" />
              </div>
            </div>
            <div>
              <h1 className="font-black text-white text-xl tracking-tight flex items-center gap-1.5">
                XOXO <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-300">SELLER</span>
              </h1>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-amber-400/10 border border-amber-400/20 text-amber-400">
                <Sparkles size={10} /> {seller?.level || 'OFFICIAL'} MERCHANT
              </span>
            </div>
          </div>

          {/* Seller Balance Mini-Card */}
          <div className="bg-gradient-to-br from-amber-500/15 via-yellow-500/5 to-transparent border border-amber-500/20 rounded-2xl p-4 mb-6 relative overflow-hidden group">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-bold text-amber-300/80 uppercase tracking-wider">Available Coins</span>
              <button 
                onClick={fetchSellerProfile}
                disabled={loadingBalance}
                title="Refresh Balance"
                className="p-1 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition"
              >
                <RefreshCw size={14} className={loadingBalance ? 'animate-spin text-amber-400' : ''} />
              </button>
            </div>
            <div className="text-2xl font-black text-white tracking-tight flex items-center gap-1.5">
              <Coins size={20} className="text-amber-400 fill-amber-400/20" />
              <span>{Number(balance).toLocaleString('en-IN')}</span>
            </div>
            <div className="text-[11px] text-slate-400 mt-1 flex items-center justify-between">
              <span>Tier: <strong className="text-white">{seller?.level || 'BRONZE'}</strong></span>
              <span>Commission: <strong className="text-amber-400">{seller?.commission_percentage || '10'}%</strong></span>
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="space-y-2">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href || (item.href === '/seller/dashboard' && location.pathname === '/seller/wallet');
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsSidebarOpen(false)}
                  className={`
                    flex items-center gap-3.5 px-4 py-3.5 rounded-2xl font-bold text-sm transition-all duration-200
                    ${isActive 
                      ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 shadow-lg shadow-amber-500/25' 
                      : 'text-slate-400 hover:text-white hover:bg-white/5 border border-transparent hover:border-white/5'}
                  `}
                >
                  <Icon size={19} className={isActive ? 'text-slate-950' : 'text-slate-400'} />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        {/* User Card & Logout */}
        <div className="pt-6 border-t border-white/10">
          <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-2xl p-3.5 mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-purple-500 to-pink-500 flex items-center justify-center font-black text-white text-sm overflow-hidden">
                {seller?.avatar_url ? (
                  <img src={seller.avatar_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  (seller?.name || 'S')[0]
                )}
              </div>
              <div className="overflow-hidden">
                <p className="font-bold text-sm text-white truncate">{seller?.name || 'Seller Account'}</p>
                <p className="text-xs text-amber-400 font-medium">ID: {seller?.xoxo_id || 'N/A'}</p>
              </div>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-2xl font-bold text-sm text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 transition duration-200"
          >
            <LogOut size={16} />
            <span>Logout Portal</span>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-y-auto max-h-screen">
        <main className="flex-1 p-4 md:p-8 lg:p-10 max-w-7xl w-full mx-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default SellerLayout;
